// src/components/admin/tabs/pointsCenter/pointsShared.js
// 积分中心共享工具：格式化 / 时段预设 / 粒度推荐（纯函数，无组件）。
import { formatMoney } from "../../adminUtils";

export const ACCENT = "var(--color-warning)";
export const COST_COLOR = "var(--color-blue-primary)";

export { formatMoney };

// ============================
// 格式化
// ============================

export const formatPoints = (value) => Math.round(Number(value || 0)).toLocaleString("zh-CN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// 轴刻度用紧凑数字：1.2万 / 3.4亿
export const formatCompact = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n) >= 100000000) return `${(n / 100000000).toFixed(1)}亿`;
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return String(Math.round(n));
};

export const formatMinutes = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("zh-CN", { maximumFractionDigits: 1 });
};

export const formatRate = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "-";
  return n.toFixed(2);
};

export const formatDateTime = (value) => {
  if (value == null || value === "") return "-";
  const date = Number.isNaN(Number(value)) ? new Date(value) : new Date(Number(value));
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("zh-CN", {
    month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
};

// ============================
// 时段预设与粒度
// ============================

const DAY_MS = 86400000;
const pad2 = (n) => String(n).padStart(2, "0");
export const dateKey = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export const RANGE_PRESETS = [
  { key: "today", label: "今天" },
  { key: "yesterday", label: "昨天" },
  { key: "7d", label: "近7天" },
  { key: "30d", label: "近30天" },
  { key: "month", label: "本月" },
  { key: "lastMonth", label: "上月" },
  { key: "year", label: "今年" },
];

export const rangeOfPreset = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case "today":
      return { from: dateKey(today), to: dateKey(today) };
    case "yesterday": {
      const d = new Date(today.getTime() - DAY_MS);
      return { from: dateKey(d), to: dateKey(d) };
    }
    case "7d":
      return { from: dateKey(new Date(today.getTime() - 6 * DAY_MS)), to: dateKey(today) };
    case "30d":
      return { from: dateKey(new Date(today.getTime() - 29 * DAY_MS)), to: dateKey(today) };
    case "month":
      return { from: dateKey(new Date(now.getFullYear(), now.getMonth(), 1)), to: dateKey(today) };
    case "lastMonth": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: dateKey(first), to: dateKey(last) };
    }
    case "year":
      return { from: `${now.getFullYear()}-01-01`, to: dateKey(today) };
    default:
      return { from: dateKey(new Date(today.getTime() - 6 * DAY_MS)), to: dateKey(today) };
  }
};

export const detectPreset = (from, to) => {
  for (const preset of RANGE_PRESETS) {
    const r = rangeOfPreset(preset.key);
    if (r.from === from && r.to === to) return preset.key;
  }
  return "custom";
};

// 粒度自动推荐：≤2 天→按小时，≤62 天→按天，≤24 个月→按月，更长→按年
export const recommendGranularity = (from, to) => {
  const span = (new Date(to).getTime() - new Date(from).getTime()) / DAY_MS;
  if (!Number.isFinite(span) || span < 0) return "day";
  if (span <= 1) return "hour";
  if (span <= 61) return "day";
  if (span <= 731) return "month";
  return "year";
};

export const GRANULARITY_OPTIONS = [
  { key: "hour", label: "按小时" },
  { key: "day", label: "按天" },
  { key: "month", label: "按月" },
  { key: "year", label: "按年" },
];

export const bucketLabel = (bucket, granularity) => {
  const text = String(bucket || "");
  if (granularity === "hour") {
    const timePart = text.includes("T") ? text.split("T")[1] : (text.includes(" ") ? text.split(" ")[1] : "");
    return (timePart || text).slice(0, 5) || text;
  }
  if (granularity === "day") return text.slice(5) || text;
  return text;
};
